/**
 * Step 1: Basic Information
 * Clean, simple form for agent details
 */

import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { SignupData } from '../AgentSignup';

interface StepOneProps {
  data: SignupData;
  updateData: (data: Partial<SignupData>) => void;
  onNext: () => void;
}

const StepOne: React.FC<StepOneProps> = ({ data, updateData, onNext }) => {
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  
  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!data.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!data.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!data.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!data.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s()-]+$/.test(data.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!data.password) {
      newErrors['password'] = 'This field is required';
    } else if (data.password.length < 8) {
      newErrors['password'] = 'Must be at least 8 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };
  
  const handleChange = (field: keyof SignupData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateData({ [field]: e.target.value });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="signup-form">
      <div className="step-header">
        <h2>Let's get you started</h2>
        <p>We'll set up your account in just 3 minutes</p>
      </div>

      <Form.Group className="mb-4">
        <Form.Label>First Name</Form.Label>
        <Form.Control
          type="text"
          value={data.firstName}
          onChange={handleChange('firstName')}
          placeholder="John"
          isInvalid={!!errors.firstName}
          autoFocus
        />
        {errors.firstName && (
          <div className="form-error">{errors.firstName}</div>
        )}
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Last Name</Form.Label>
        <Form.Control
          type="text"
          value={data.lastName}
          onChange={handleChange('lastName')}
          placeholder="Smith"
          isInvalid={!!errors.lastName}
        />
        {errors.lastName && (
          <div className="form-error">{errors.lastName}</div>
        )}
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Email Address</Form.Label>
        <Form.Control
          type="email"
          value={data.email}
          onChange={handleChange('email')}
          placeholder="john@realestate.com"
          isInvalid={!!errors.email}
        />
        <div className="form-helper">
          This will be your login email
        </div>
        {errors.email && (
          <div className="form-error">{errors.email}</div>
        )}
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Phone Number</Form.Label>
        <Form.Control
          type="tel"
          value={data.phone}
          onChange={handleChange('phone')}
          placeholder="(555) 123-4567"
          isInvalid={!!errors.phone}
        />
        <div className="form-helper">
          We'll only use this if clients need help
        </div>
        {errors.phone && (
          <div className="form-error">{errors.phone}</div>
        )}
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Create Password</Form.Label>
        <Form.Control
          type="password"
          value={data.password}
          onChange={handleChange('password')}
          placeholder="••••••••"
          isInvalid={!!errors.password}
        />
        <div className="form-helper">
          At least 8 characters
        </div>
        {errors.password && (
          <div className="form-error">{errors.password}</div>
        )}
      </Form.Group>

      <div className="step-buttons">
        <div></div>
        <Button type="submit" className="btn-next">
          Continue
          <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
        </Button>
      </div>
    </Form>
  );
};

export default StepOne;